/**
 * Domain types for Edit Chat functionality
 * Reuses existing types from createChat and chat modules
 */

import type { ChatType, ChatListItem } from './chat';
import type {
  BaseSelectOption,
  BuyerSelectOption,
  UserSelectOption,
  BuyerUserSelectOption,
  SupplierUserSelectOption,
  CreatedContextOption,
  CreatedContext,
  DeliveryDay,
  CutOffTimeType,
  WeekdayBasedCutoffSettings,
  CreateChatFormData,
} from './createChat';

// GraphQL-compatible types matching the server schema
export type ChatStatus = 'active' | 'archived' | 'pending' | 'requested';
export type GraphQLCutOffTimeType = 'flexible' | 'noCutoff' | 'strict';
export type GraphQLChatType = 'ordering' | 'support';

// Edit Chat Tab Names
//
// DECISION NOTE (issue #124): The legacy Order Guide editor is intentionally
// retired and is deliberately absent from this enum. Do NOT re-add an
// `ORDER_GUIDE` tab or port the old product CRUD / batch-upload UI as "parity" —
// future Portal Order Guide content will come from Retool via a separate
// read-only integration. Because `order-guide` is no longer a member here, the
// stale `#order-guide` hash resolves to Participants (see resolveEditChatTab in
// apps/admin-web/src/pages/ChatsPage/hooks/useActiveTabName.ts).
export enum EditChatTabNames {
  PARTICIPANTS = 'participants',
  SETTINGS = 'settings',
  CHAT_HISTORY = 'chat-history',
}

// Edit Chat Form Data (extends CreateChatFormData with additional fields for editing)
export interface EditChatFormData extends Omit<
  CreateChatFormData,
  'cutoffType' | 'chatType'
> {
  // Chat identification
  id: string;
  chatStatus: ChatStatus;

  // Override types to match GraphQL schema
  cutoffType: GraphQLCutOffTimeType;
  chatType: {
    value: GraphQLChatType;
    label: string;
  } | null;

  // Original data for comparison
  originalBuyerId?: string;
  originalSupplierId?: string;
  originalChatType?: ChatType;
  originalCreatedContext?: string;
}

// API input for updating chat
export interface UpdateChatInput {
  id: string;
  chatName?: string | null;
  restaurantName?: string | null;
  deliveryAddress?: string | null;
  customerNumber?: string | null;
  additionalEmails?: string[];
  buyerId?: string | null;
  subCatalogIds?: string[];
  enabled?: boolean;
}

// A user attached to a chat (buyer or distributor side)
export interface ChatUser {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  supplier: boolean;
  // The supplier bot user is also `supplier: true`; this flag lets the
  // participants view filter it out (see SupplierUsersSection).
  botUser?: boolean;
  orderPreferences?: {
    whatsApp?: boolean;
    sms?: boolean;
    fax?: boolean;
  };
}

// Chat details response from API (extends ChatListItem with additional edit-specific data)
export interface ChatDetailsResponse extends Omit<ChatListItem, 'type'> {
  status: ChatStatus;
  type: ChatType;
  createdAt: string;
  updatedAt: string;
  additionalEmails: string[];

  // Whether this is a Puppy/Dogfood chat. Used to enforce the Puppy-mode
  // access boundary when a chat is opened via a direct URL.
  isPuppy?: boolean;

  // How the chat was created. Drives the requested-chat fulfilment guidance
  // (see requestedChatLifecycle util). Absent for chats created before the
  // backend started stamping createdContext.
  createdContext?: CreatedContext | null;

  // Extended user information for editing
  users: ChatUser[];

  // The distributor's Supplier *entity* id (for catalog/entity lookups).
  // Distinct from `supplierEntity.id`, which the detail mapper fills from the
  // supplier bot *user* id.
  supplierEntityId?: string;

  // Configuration
  subCatalogs?: Array<{
    id: string;
    name: string;
    supplierId: string;
  }>;
  priceVisibility?: {
    displayPricesOverride?: boolean;
  };
  deliverySettings?: {
    hasCustomSettings: boolean;
    deliveryDays?: DeliveryDay[];
    cutoffType?: GraphQLCutOffTimeType;
    cutoffTimes?: WeekdayBasedCutoffSettings;
  };
  movSettings?: {
    amount?: string;
    currency?: string;
  };
}

// Chat history types

// How a history message should be rendered. Order messages carry operational
// order context, media messages carry an attachment, plain messages are text.
export type ChatHistoryMessageKind = 'plain' | 'media' | 'order';

// A media attachment (image, PDF, …) attached to a message.
export interface ChatHistoryAttachment {
  url: string;
}

// A single product line on an order-carrying message.
export interface ChatHistoryOrderProduct {
  id: string;
  name: string;
  unit: string;
  amount: number;
}

// Operational order context exposed by an order message. Mirrors what active
// legacy shows so CS can inspect the order without leaving the chat history.
export interface ChatHistoryOrderDetails {
  id: string;
  createdAt: string;
  referenceId: string;
  status: string;
  supplierStatus: string;
  // The order note / free-text body submitted with the order.
  notes: string;
  // The user who placed the order.
  sender: string;
  // The supplier the order was placed with.
  supplier: string;
  products: ChatHistoryOrderProduct[];
}

export interface ChatHistoryMessage {
  id: string;
  dateTime: string;
  from: string;
  to: string;
  message: string;
  kind: ChatHistoryMessageKind;
  // Present when the message carries a media attachment.
  attachment: ChatHistoryAttachment | null;
  // Present when the message carries an order (kind === 'order').
  order: ChatHistoryOrderDetails | null;
}

export interface ChatHistoryResponse {
  messages: ChatHistoryMessage[];
  totalCount: number;
}

// Hook configuration types (reuse pattern from createChat)
export interface EditChatConfig {
  onSuccess?: (chat: ChatDetailsResponse) => void;
  onError?: (error: Error) => void;
  captureException?: (error: Error) => void;
}

// Validation rules for edit chat (extends create chat validation concept)
export interface EditChatValidationRules {
  canEditBuyer: boolean;
  canEditSupplier: boolean;
  canEditChatType: boolean;
  canEditCreatedContext: boolean;
  customerNumberMandatory: boolean;
  requiresBuyerUsers: boolean;
  requiresSupplierUsers: boolean;
}

// Navigation and tab management
export interface EditChatTabConfig {
  name: EditChatTabNames;
  title: string;
  formFieldNames: string[];
  isEnabled: boolean;
  hasChanges: boolean;
  isValid: boolean;
}

// User management operations
export interface ChatUserOperation {
  type: 'add' | 'remove';
  userId: string;
  userType: 'buyer' | 'supplier';
}

// Duplicate chat functionality
// NOTE (issue #124): no order-guide option — the retired Order Guide editor is
// not duplicated. Ordinary same-buyer / same-distributor duplication only.
export interface DuplicateChatOptions {
  chatId: string;
  includeParticipants: boolean;
  includeSettings: boolean;
}

// Response type reuses existing pattern
export interface DuplicateChatResponse {
  id: string;
  chatName: string;
  status: ChatStatus;
}

// Additional form types for complete EditChat functionality
export interface EditChatFormFields {
  // Chat participants
  buyer: BuyerSelectOption | null;
  supplier: UserSelectOption | null;
  buyerUsers: BuyerUserSelectOption[] | null;
  supplierUsers: SupplierUserSelectOption[] | null;
  deselectedOrderPreferences?: string[];

  // Chat settings
  chatName?: string | null;
  deliveryAddress?: string | null;
  restaurantName?: string | null;
  customerNumber?: string | null;
  chatType: BaseSelectOption | null;
  customerLevelMovAmount?: string | null;
  customerLevelMovCurrency?: string | null;
  additionalEmails: string;
  subCatalog: BaseSelectOption[] | null;
  createdContext: CreatedContextOption | null;
  visibleChatPrices?: boolean;
  isPriceOverrideEnabled?: boolean;

  // Edit-specific fields
  isPuppy?: boolean | null;

  // Delivery settings
  deliveryDays?: DeliveryDay[];
  cutoffType?: CutOffTimeType;
  cutoffTimes?: WeekdayBasedCutoffSettings;
}

// Order preference types
export enum FormattedOrderPreferenceTypesEnum {
  WHATSAPP = 'WHATSAPP',
  SMS = 'SMS',
  FAX = 'FAX',
}

export interface FormattedOrderPreference {
  type: FormattedOrderPreferenceTypesEnum;
  value: string;
}

// User metadata for select options
export interface UserSelectOptionMetaData {
  name: string | null | undefined;
  supplierId: string | null | undefined;
  phone: string | null | undefined;
  email: string | null | undefined;
  orderPreference: any | null | undefined; // OrderPreferenceFragment
  formattedOrderPreference: FormattedOrderPreference[] | undefined;
  customerNumberMandatory: boolean | null | undefined;
}

export interface BuyerSelectOptionMetaData {
  fullAddress: string;
}

export interface SupplierUserSelectOptionMetaData {
  supplier: boolean;
  phone: string;
  formattedOrderPreference: FormattedOrderPreference[];
  email: string | undefined | null;
}

export interface BuyerUserSelectOptionMetaData {
  supplier: boolean;
  phone: string;
}

// Tab panel configuration
export interface EditChatTabPanelConfig {
  name: EditChatTabNames;
  title: string;
  formFieldNames: string[];
  width?: string;
}

// Form validation
export interface EditChatValidationConfig {
  chatStatus: ChatStatus;
  customerNumberMandatory: boolean;
}

// NOTE (issue #124): product CRUD types for the retired Order Guide editor
// (ChatProduct / ProductUpdateInput) were removed. Do not reintroduce them to
// rebuild an in-portal Order Guide editor — that content will come from Retool.

// Delivery settings
export interface DeliverySettingFragment {
  deliveryDays?: DeliveryDay[];
  cutoffType?: CutOffTimeType;
  cutoffTimes?: WeekdayBasedCutoffSettings;
  hasCustomSettings?: boolean;
}

// Context providers
export interface ChatPageContextData {
  pageErrorFields: string[];
  setPageErrorFields: (fields: string[]) => void;
}

export interface DeliverySettingContextData {
  chatDeliverySetting: DeliverySettingFragment | null;
  updateDeliverySetting: (setting: DeliverySettingFragment) => void;
}

// Wizard context for create chat flow
export interface WizardContextData {
  hasSupplierEntity: boolean;
  setHasSupplierEntity: (has: boolean) => void;
  createdContext?: any;
}

// API operation types
export interface ChatUserOperationInput {
  chatId: string;
  userId: string;
  userType: 'buyer' | 'supplier';
  operation: 'add' | 'remove';
}

export interface SubCatalogOperationInput {
  chatId: string;
  subCatalogIds: string[];
  operation: 'add' | 'remove';
}

export interface UpdateChatPricesVisibilityInput {
  chatId: string;
  displayPricesOverride: boolean;
}

export interface UpdateNotificationPreferencesInput {
  chatId: string;
  userId: string;
  preferences: {
    whatsApp?: boolean;
    sms?: boolean;
    fax?: boolean;
  };
}

// MOV (Minimum Order Value) settings
export interface SupplierBuyerMovInput {
  supplierId: string;
  buyerId: string;
  amount: string;
  currency: string;
}

export interface RemoveSupplierBuyerMovInput {
  supplierId: string;
  buyerId: string;
}
